import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, DestroyRef, OnInit, inject, input } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslateModule } from '@ngx-translate/core';
import { BehaviorSubject, Observable, combineLatest } from 'rxjs';
import { map, startWith, switchMap } from 'rxjs/operators';

import { AccountFacade } from 'ish-core/facades/account.facade';
import { CheckoutFacade } from 'ish-core/facades/checkout.facade';
import { ProductContextFacade } from 'ish-core/facades/product-context.facade';
import { IconModule } from 'ish-core/icon.module';
import { ProductHelper } from 'ish-core/models/product/product.model';
import { whenFalsy } from 'ish-core/utils/operators';

/**
 * Displays an add to cart button with an icon or a text label. After clicking the button a loading animation is displayed
 *
 * @example
 * <ish-product-add-to-basket
    [cssClass]="'btn-lg btn-block'"
  ></ish-product-add-to-basket>
 */
@Component({
  selector: 'ish-product-add-to-basket',
  templateUrl: './product-add-to-basket.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, IconModule, TranslateModule],
})
export class ProductAddToBasketComponent implements OnInit {
  private checkoutFacade = inject(CheckoutFacade);
  private accountFacade = inject(AccountFacade);
  private context = inject(ProductContextFacade);

  /**
   * when 'icon', the button label is an icon, otherwise it is text
   */
  readonly displayType = input<'icon' | 'link' | 'plaintext'>('link');
  /**
   * additional css styling
   */
  readonly cssClass = input<string>(undefined);
  /**
   * hidden for screen readers
   */
  readonly ariaHidden = input(false);
  /**
   * prevent the add to basket action dispatch
   */
  readonly preventAddToBasket = input(false);
  // readonly addToCartButton = input<boolean>();
  readonly addToCartButton = input(false);

  basketLoading$: Observable<boolean>;
  visible$: Observable<boolean>;
  translationKey$: Observable<string>;

  buttonDisabled$: Observable<boolean>;

  /**
   * fires 'true' after add To Cart is clicked and basket is loading
   */
  displaySpinner$ = new BehaviorSubject(false);

  private destroyRef = inject(DestroyRef);

  ngOnInit() {
    this.visible$ = this.showAddToBasket$();

    this.translationKey$ = this.context.select('product').pipe(
      map(product =>
        ProductHelper.isRetailSet(product) ? 'product.add_to_cart.retailset.link' : 'product.add_to_cart.link'
      ),
      startWith('product.add_to_cart.link')
    );

    this.basketLoading$ = this.checkoutFacade.basketLoading$;

    // update emitted to display spinning animation
    this.basketLoading$.pipe(whenFalsy(), takeUntilDestroyed(this.destroyRef)).subscribe(this.displaySpinner$); // false

    const hasQuantityError$ = this.context.select('hasQuantityError');
    const hasProductError$ = this.context.select('hasProductError');
    const hasNoQuantity$ = this.context.select('quantity').pipe(map(quantity => quantity <= 0));
    const loading$ = this.displaySpinner$.pipe(startWith(false));
    // disable button when spinning, in case of an error (quick order) or during login or basket loading

    this.buttonDisabled$ = combineLatest([
      loading$,
      hasQuantityError$,
      hasProductError$,
      hasNoQuantity$,
      this.accountFacade.userLoading$,
      this.basketLoading$,
    ]).pipe(map(conditions => conditions.some(c => c)));
  }

  addToBasket() {
    if (!this.preventAddToBasket()) {
      this.context.addToBasket();
    }
    this.displaySpinner$.next(true);
  }

  get displayIcon(): boolean {
    return this.displayType() === 'icon';
  }

  get displayPlainText(): boolean {
    return this.displayType() === 'plaintext';
  }

  showAddToBasket$() {
    return this.checkoutFacade.currentRouteUrl$.pipe(
      switchMap(routeUrl => {
        if (/^\/.*compare.*$/.test(routeUrl)) {
          // Compare page → need price + product + addToBasket
          return combineLatest([
            this.context.select('prices'),
            this.context.select('product'),
            this.context.select('displayProperties', 'addToBasket'),
          ]).pipe(
            map(
              ([price, product, displayAddToBasket]) =>
                price !== undefined && product !== undefined && displayAddToBasket
            )
          );
        } else {
          // Other pages → only care about addToBasket
          return this.context.select('displayProperties', 'addToBasket');
        }
      })
    );
  }
}
